import { SceneCategory } from './IMaterialPass';
import { Geometry } from '../geometry/Geometry';
import shader from '../shaders/scatter-instanced.wgsl';
import { Renderer } from '..';
import { SharedUniformsTracker } from './SharedUniformsTracker';
import { Mesh } from '../core/Mesh';
import { Camera } from '../core/Camera';
import { StandardMaterial } from './uniforms/StandardMaterial';
import { Lighting } from './uniforms/Lighting';
import { EnvironmentUniforms } from './uniforms/EnvironmentUniforms';
import { StandardPassBase } from './StandardPassBase';
import { composeShader } from '../utils/shaderDefines';
import { IScatterInstanceGroup } from '../../types/interfaces';
import { Vector3 } from 'rewild-common';
import { ShaderDefines } from '../utils/shaderDefines';
import type { ScatterWind } from '../renderers/terrain/ScatterLayers';

const _viewerLocal = new Vector3();

const materialGroupIndex = 0;
const instanceGroupIndex = 1;
const lightingGroup = 2;
const environmentGroup = 3;

// projMatrix + modelViewMatrix + nodeMatrix + band + debug + wind + windParams
// + windOrigin.
export const SCATTER_UNIFORM_BYTES = 64 * 3 + 80;

// posScale + rotation + params, matching ScatterInstance in the shader.
export const SCATTER_GPU_STRIDE = 12;

/**
 * Scatter instancing — StandardPass's shading over one draw per chunk.
 *
 * Where StandardInstancedPass rebuilds a model-view matrix per instance every
 * frame, this reads a persistent per-instance transform uploaded when the chunk
 * loaded, and takes the camera from a per-draw uniform instead. That is what
 * lets a forest cost one buffer write per chunk per frame rather than one per
 * tree.
 *
 * A draw's instances live on the group it is drawing (see IScatterInstanceGroup)
 * rather than on the pass, because a pass is shared by every chunk that grows
 * the layer and each of those owns its own buffer.
 */
export class ScatterInstancedPass extends StandardPassBase {
  profileCategory: SceneCategory = 'scatter';

  pipeline: GPURenderPipeline;
  perMeshTracker: SharedUniformsTracker;
  material: StandardMaterial;
  lightingUniforms: Lighting;
  environmentUniforms: EnvironmentUniforms;

  constructor() {
    super();
    this.material = new StandardMaterial(materialGroupIndex);
    this.lightingUniforms = new Lighting(lightingGroup);
    this.environmentUniforms = new EnvironmentUniforms(
      environmentGroup,
      true,
      true,
      true
    );

    this.perMeshTracker = new SharedUniformsTracker(this, [
      this.material,
      this.lightingUniforms,
      this.environmentUniforms,
    ]);
  }

  private _wind: ScatterWind | null = null;

  /**
   * The layer's wind block, or null to draw rigid. Compiles the wind variant:
   * COLOR_0 is bound and read as bend weights — R bend, G phase, B flutter —
   * and never as a tint, so `vertexColors` has no effect while this is set.
   * Only for geometry that carries COLOR_0; a model without it has no weights
   * to bend by and draws rigid whatever the layer says.
   */
  get wind(): ScatterWind | null {
    return this._wind;
  }

  set wind(value: ScatterWind | null) {
    if (value === this._wind) return;
    this._wind = value;
    this.invalidatePipeline();
  }

  protected bindsVertexColors(): boolean {
    return this._wind !== null || super.bindsVertexColors();
  }

  protected shaderDefines(): ShaderDefines {
    return { ...super.shaderDefines(), HAS_WIND: this._wind !== null };
  }

  protected invalidatePipeline(): void {
    this.requiresRebuild = true;
    for (const uniform of this.perMeshTracker.uniforms)
      uniform.requiresBuild = true;
  }

  init(renderer: Renderer): void {
    this.requiresRebuild = false;
    const { device, sceneColorFormat } = renderer;
    const module = device.createShaderModule({
      label: 'scatter instanced shader',
      code: composeShader([shader], this.shaderDefines()),
    });

    this.pipeline = device.createRenderPipeline({
      label: 'Scatter Instanced Pass',
      layout: 'auto',
      vertex: {
        entryPoint: this.vertexEntryPoint(),
        module,
        buffers: this.vertexBufferLayouts(),
      },
      fragment: {
        entryPoint: 'fs',
        module,
        targets: renderer.sceneTargets(
          { format: sceneColorFormat, blend: this.blendState() },
          !this.transparent
        ),
      },
      multisample: { count: renderer.sampleCount },
      primitive: this.primitiveState(),
      depthStencil: this.depthStencilState(),
    });
  }

  dispose(): void {
    this.perMeshTracker.dispose();
  }

  /** The layout a group's own bind group is built against. Exposed because the
   *  instance buffer belongs to the group, not to the pass. */
  instanceBindGroupLayout(): GPUBindGroupLayout {
    return this.pipeline.getBindGroupLayout(instanceGroupIndex);
  }

  render(
    renderer: Renderer,
    pass: GPURenderPassEncoder,
    camera: Camera,
    meshes: Mesh[],
    geometry: Geometry
  ): void {
    pass.setPipeline(this.pipeline);
    this.setVertexBuffers(pass, geometry);
    this.perMeshTracker.prepareMeshUniforms(renderer, pass, camera, meshes);

    const numIndices = geometry.indices!.length;
    const projection = camera.projectionMatrix.elements;
    const cameraWorld = camera.transform.matrixWorld.elements;

    for (let i = 0; i < meshes.length; i++) {
      const group = meshes[i] as unknown as IScatterInstanceGroup;
      if (group.instanceCount === 0) continue;

      // Chunk transforms only translate, so the viewer lands in chunk-local
      // space by subtraction rather than an inverse per group.
      const world = group.transform.matrixWorld.elements;
      _viewerLocal.set(
        cameraWorld[12] - world[12],
        cameraWorld[13] - world[13],
        cameraWorld[14] - world[14]
      );
      group.selectInstances(_viewerLocal, renderer.frustum);
      if (group.rangeCount === 0) continue;

      const bindGroup = group.prepareInstances(renderer, this);
      if (!bindGroup) continue;

      // Only the model-view changes per frame; the projection, node matrix and
      // LOD band ride along because one small write beats several.
      group.writeFrameUniforms(
        renderer,
        projection,
        group.transform.modelViewMatrix.elements
      );

      pass.setBindGroup(instanceGroupIndex, bindGroup);
      for (let r = 0; r < group.rangeCount; r++)
        pass.drawIndexed(
          numIndices,
          group.rangeCounts[r],
          0,
          0,
          group.rangeStarts[r]
        );
    }
  }
}
